'use client'

import {
  BarChart3,
  CalendarDays,
  Check,
  ChevronRight,
  GraduationCap,
  ImageIcon,
  Layers3,
  Package,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react'

export type ChatEntityKind =
  | 'funnel'
  | 'product'
  | 'event'
  | 'course'
  | 'challenge'
  | 'community'
  | 'creative'
  | 'campaign'

export type ChatEntityResultItem = {
  id: string
  title: string
  subtitle?: string
  description?: string
  thumbnailUrl?: string
  badge?: string
  value: string
  actionLabel?: string
  meta?: Record<string, any>
}

export type ChatEntityResultsPayload = {
  kind: ChatEntityKind
  title?: string
  subtitle?: string
  emptyTitle?: string
  emptyDescription?: string
  items: ChatEntityResultItem[]
}

type ChatEntityResultsProps = {
  payload: ChatEntityResultsPayload
  onSelect?: (item: ChatEntityResultItem) => void
}

function getKindIcon(kind: ChatEntityKind) {
  switch (kind) {
    case 'funnel':
      return <Layers3 className="h-4 w-4" />
    case 'product':
      return <Package className="h-4 w-4" />
    case 'event':
      return <CalendarDays className="h-4 w-4" />
    case 'course':
      return <GraduationCap className="h-4 w-4" />
    case 'challenge':
      return <Trophy className="h-4 w-4" />
    case 'community':
      return <Users className="h-4 w-4" />
    case 'creative':
      return <ImageIcon className="h-4 w-4" />
    case 'campaign':
      return <BarChart3 className="h-4 w-4" />
    default:
      return <Sparkles className="h-4 w-4" />
  }
}

function getKindLabel(kind: ChatEntityKind) {
  switch (kind) {
    case 'funnel':
      return 'Embudos'
    case 'product':
      return 'Productos'
    case 'event':
      return 'Eventos'
    case 'course':
      return 'Cursos'
    case 'challenge':
      return 'Retos'
    case 'community':
      return 'Comunidades'
    case 'creative':
      return 'Piezas'
    case 'campaign':
      return 'Campañas'
    default:
      return 'Resultados'
  }
}

function EntityThumbnail({
  item,
  kind,
}: {
  item: ChatEntityResultItem
  kind: ChatEntityKind
}) {
  if (item.thumbnailUrl) {
    return (
      <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-black/5 bg-neutral-100 dark:border-white/10 dark:bg-neutral-900">
        <img
          src={item.thumbnailUrl}
          alt={item.title}
          className="h-full w-full object-cover"
        />

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-white/10" />

        {item.badge ? (
          <div className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-800 shadow-sm backdrop-blur dark:bg-neutral-950/80 dark:text-neutral-100">
            {item.badge}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-neutral-200 bg-gradient-to-br from-neutral-100 via-white to-yellow-50 dark:border-neutral-800 dark:from-neutral-900 dark:via-neutral-950 dark:to-yellow-950/20">
      <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-yellow-300/20 blur-2xl" />
      <div className="absolute -bottom-10 left-8 h-24 w-24 rounded-full bg-orange-300/20 blur-2xl" />

      <div className="absolute bottom-4 left-4 right-4">
        <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-2xl bg-neutral-950 text-white shadow-sm dark:bg-white dark:text-neutral-950">
          {getKindIcon(kind)}
        </div>

        <p className="line-clamp-2 text-base font-black leading-tight tracking-tight text-neutral-950 dark:text-neutral-50">
          {item.title}
        </p>
      </div>
    </div>
  )
}

export function ChatEntityResults({
  payload,
  onSelect,
}: ChatEntityResultsProps) {
  const items = Array.isArray(payload?.items) ? payload.items : []
  const kind = payload?.kind || 'funnel'

  if (!items.length) {
    return (
      <div className="mt-3 rounded-3xl border border-dashed border-neutral-250 bg-white p-5 text-center shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-neutral-100 text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">
          {getKindIcon(kind)}
        </div>

        <div className="text-sm font-bold text-neutral-950 dark:text-neutral-50">
          {payload.emptyTitle || `No encontré ${getKindLabel(kind).toLowerCase()}`}
        </div>

        <div className="mx-auto mt-1 max-w-sm text-xs leading-5 text-neutral-500 dark:text-neutral-400">
          {payload.emptyDescription ||
            'Todavía no hay resultados disponibles para mostrar aquí.'}
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
      <div className="border-b border-neutral-100 bg-gradient-to-br from-yellow-50 via-white to-orange-50 p-4 dark:border-neutral-800 dark:from-yellow-500/10 dark:via-neutral-950 dark:to-orange-500/10">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-neutral-950 text-white shadow-sm dark:bg-white dark:text-neutral-950">
            {getKindIcon(kind)}
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm font-black tracking-tight text-neutral-950 dark:text-neutral-50">
              {payload.title || getKindLabel(kind)}
            </div>

            {payload.subtitle ? (
              <div className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                {payload.subtitle}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid gap-3 p-3 sm:grid-cols-2">
        {items.map((item) => (
          <button
            key={`${kind}-${item.id}`}
            type="button"
            onClick={() => onSelect?.(item)}
            className="group rounded-3xl border border-neutral-200 bg-white p-2 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-lg dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700"
          >
            <EntityThumbnail item={item} kind={kind} />

            <div className="px-1 pb-1 pt-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-1 text-sm font-bold text-neutral-950 dark:text-neutral-50">
                    {item.title}
                  </div>

                  {item.subtitle ? (
                    <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      {item.subtitle}
                    </div>
                  ) : null}
                </div>

                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-400 transition group-hover:border-neutral-950 group-hover:bg-neutral-950 group-hover:text-white dark:border-neutral-800 dark:group-hover:border-white dark:group-hover:bg-white dark:group-hover:text-neutral-950">
                  <ChevronRight className="h-3.5 w-3.5" />
                </div>
              </div>

              {item.description ? (
                <div className="mt-2 line-clamp-2 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                  {item.description}
                </div>
              ) : null}

              <div className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-2xl bg-neutral-950 px-3 text-xs font-bold text-white transition group-hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:group-hover:bg-neutral-200">
                <Check className="h-3.5 w-3.5" />
                {item.actionLabel || 'Elegir'}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}